/**
 * Text Element Player
 * Renders text with inline markdown support via react-markdown
 */

import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import { cn } from '@ai-content-hub/content-kit';
import type { ElementPlayerProps } from '@ai-content-hub/content-kit';
import type { TextElementData } from './schema';

export interface TextPlayerProps extends ElementPlayerProps<TextElementData> { }

export const TextPlayer: React.FC<TextPlayerProps> = ({ data, className }) => {
    // Validate data exists
    if (!data) {
        return (
            <div className={cn('text-muted-foreground italic', className)}>
                No text content
            </div>
        );
    }

    const { text = '', variant = 'body', alignment = 'left' } = data;

    // Handle empty text
    if (!text) {
        return (
            <div className={cn('text-muted-foreground italic', className)}>
                Empty text
            </div>
        );
    }

    // Variant classes
    const variantClasses: Record<string, string> = {
        body: 'text-base leading-relaxed',
        heading: 'text-2xl font-bold leading-tight',
        caption: 'text-sm text-muted-foreground',
        label: 'text-sm font-medium uppercase tracking-wide',
    };

    // Alignment classes
    const alignmentClasses: Record<string, string> = {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
    };

    return (
        <div
            className={cn(
                'text-element text-element-markdown',
                variantClasses[variant] || variantClasses.body,
                alignmentClasses[alignment] || alignmentClasses.left,
                // Flatten markdown output: remove paragraph margins for inline-like rendering
                '[&>p]:m-0 [&>p:last-child]:mb-0',
                className
            )}
        >
            <ReactMarkdown>{text}</ReactMarkdown>
        </div>
    );
};

export default TextPlayer;
